import React from "react";
import { Check, Clock, CalendarDays, Store, CreditCard } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";

const cardIcons = [Clock, CalendarDays];

export default function Cards() {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();

  return (
    <section id="card" className="py-20 lg:py-28 bg-secondary/60">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-primary font-semibold text-sm tracking-[0.18em] uppercase">
            {t.cards.eyebrow}
          </span>
          <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[30px] sm:text-[40px] leading-tight tracking-[-0.02em]">
            {t.cards.title}
          </h2>
          <p className="mt-4 text-[16px] text-slate-700">{t.cards.subtitle}</p>
        </div>

        <div ref={ref} className="mt-14 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {t.cards.items.map((card, i) => {
            const Icon = cardIcons[i];
            const featured = i === 1;
            return (
              <div
                key={i}
                className={`relative rounded-3xl p-7 lg:p-8 hsc-reveal ${visible ? "is-visible" : ""} ${
                  featured
                    ? "bg-slate-900 text-white shadow-[0_30px_70px_-30px_rgba(13,100,120,0.8)] md:-translate-y-3"
                    : "bg-white border border-slate-100 hsc-card-shadow"
                }`}
                style={{ animationDelay: `${i * 120}ms` }}
              >
                {featured && (
                  <span className="absolute top-6 right-6 text-[11px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-3 py-1 rounded-full">
                    {t.cards.popular}
                  </span>
                )}
                <span
                  className={`grid place-items-center w-13 h-13 w-14 h-14 rounded-2xl ${
                    featured ? "bg-white/10 text-cyan-300" : "bg-primary/10 text-primary"
                  }`}
                >
                  <Icon className="w-7 h-7" strokeWidth={2} />
                </span>

                <h3 className={`mt-5 font-display font-extrabold text-2xl ${featured ? "text-white" : "text-slate-900"}`}>
                  {card.name}
                </h3>
                <p className={`text-[14px] mt-1 ${featured ? "text-slate-300" : "text-slate-500"}`}>
                  {card.duration}
                </p>

                <div className="mt-6 flex items-end gap-1">
                  <span className={`font-display font-extrabold text-5xl ${featured ? "text-white" : "text-slate-900"}`}>
                    €{card.price}
                  </span>
                  <span className={`mb-2 text-sm ${featured ? "text-slate-400" : "text-slate-500"}`}>
                    / {t.cards.perDay}
                  </span>
                </div>

                <div
                  className={`mt-3 inline-flex items-center gap-2 text-[12.5px] font-medium px-3 py-1.5 rounded-full ${
                    featured ? "bg-white/10 text-cyan-200" : "bg-primary/5 text-primary"
                  }`}
                >
                  {i === 0 ? <CreditCard className="w-3.5 h-3.5" /> : <Store className="w-3.5 h-3.5" />}
                  {card.note}
                </div>

                <ul className="mt-6 space-y-3">
                  {card.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <span
                        className={`grid place-items-center w-5 h-5 rounded-full shrink-0 mt-0.5 ${
                          featured ? "bg-cyan-400/20 text-cyan-300" : "bg-primary/10 text-primary"
                        }`}
                      >
                        <Check className="w-3 h-3" strokeWidth={3.5} />
                      </span>
                      <span className={`text-[14.5px] ${featured ? "text-slate-200" : "text-slate-700"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-[13px] text-slate-500 max-w-xl mx-auto">{t.cards.finePrint}</p>
      </div>
    </section>
  );
}
